/**
 * Thin fetch wrapper. Every request carries the session cookie and every
 * non-2xx response becomes an ApiError carrying the server's Albanian message,
 * so screens can show it directly instead of inventing their own text.
 */

export class ApiError extends Error {
  // Declared explicitly rather than as a constructor parameter property:
  // the client tsconfig sets erasableSyntaxOnly, which forbids that shorthand.
  readonly status: number;

  constructor(status: number, message: string) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
  }
}

const BASE = '/api';

async function handle(res: Response): Promise<unknown> {
  if (res.status === 204) return null;

  const contentType = res.headers.get('content-type') ?? '';
  const isJson = contentType.includes('application/json');
  const payload = isJson ? await res.json() : await res.text();

  if (!res.ok) {
    const message =
      isJson && payload && typeof payload === 'object' && 'error' in payload
        ? String((payload as { error: unknown }).error)
        : `Kërkesa dështoi (${res.status})`;
    throw new ApiError(res.status, message);
  }
  return payload;
}

export async function apiGet<T>(path: string, params?: Record<string, unknown>): Promise<T> {
  const url = new URL(BASE + path, window.location.origin);
  if (params) {
    for (const [key, value] of Object.entries(params)) {
      if (value !== undefined && value !== null && value !== '') {
        url.searchParams.set(key, String(value));
      }
    }
  }
  const res = await fetch(url.toString(), { credentials: 'same-origin' });
  return (await handle(res)) as T;
}

export async function apiPostJson<T>(path: string, body: unknown): Promise<T> {
  const res = await fetch(BASE + path, {
    method: 'POST',
    credentials: 'same-origin',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(body),
  });
  return (await handle(res)) as T;
}

export async function apiPutJson<T>(path: string, body: unknown): Promise<T> {
  const res = await fetch(BASE + path, {
    method: 'PUT',
    credentials: 'same-origin',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(body),
  });
  return (await handle(res)) as T;
}

/** Multipart, for the forms that carry an invoice file alongside their fields. */
export async function apiSendForm<T>(
  path: string,
  form: FormData,
  method: 'POST' | 'PUT' = 'POST',
): Promise<T> {
  const res = await fetch(BASE + path, {
    method,
    credentials: 'same-origin',
    body: form,
  });
  return (await handle(res)) as T;
}

export async function apiDelete<T>(path: string): Promise<T> {
  const res = await fetch(BASE + path, { method: 'DELETE', credentials: 'same-origin' });
  return (await handle(res)) as T;
}

/**
 * Trigger a browser download for an endpoint that streams a file.
 * Used by invoice download and Excel export, which both need the session
 * cookie and therefore cannot be a plain <a href> to a different origin.
 */
export async function apiDownload(path: string, params?: Record<string, unknown>): Promise<void> {
  const url = new URL(BASE + path, window.location.origin);
  if (params) {
    for (const [key, value] of Object.entries(params)) {
      if (value !== undefined && value !== null && value !== '') {
        url.searchParams.set(key, String(value));
      }
    }
  }

  const res = await fetch(url.toString(), { credentials: 'same-origin' });
  if (!res.ok) {
    await handle(res); // throws with the server's message
    return;
  }

  const blob = await res.blob();
  const filename = filenameFromDisposition(res.headers.get('content-disposition')) ?? 'download';
  const objectUrl = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = objectUrl;
  anchor.download = filename;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  URL.revokeObjectURL(objectUrl);
}

function filenameFromDisposition(header: string | null): string | null {
  if (!header) return null;
  // Prefer the RFC 5987 form, which survives Albanian characters.
  const utf8 = /filename\*=UTF-8''([^;]+)/i.exec(header);
  if (utf8?.[1]) return decodeURIComponent(utf8[1]);
  const plain = /filename="([^"]+)"/i.exec(header);
  return plain?.[1] ?? null;
}
